/**
 * Tipos y llamada REALES de `consola/panel` (`apps/api/src/panel/panel.controller.ts` +
 * `panel.service.ts`). Mismo criterio que el resto de los clientes reales de este directorio.
 *
 * 🔴 **LO QUE ESTE ENDPOINT *NO* DEVUELVE, y es lo primero que hay que saber antes de escribir
 * la pantalla.** `Panel.tsx` en su versión mock mostraba SEIS cosas; este endpoint cubre cinco:
 *
 * ```
 *   cifras (disponibles, suscritos, buscando, sin conductor hoy,
 *           en cola de verificación, incidentes abiertos)          SÍ — contadores propios
 *   reparto del período (ofertas / ganados / tasa por conductor)   SÍ — `tasaVictoria.reparto`
 *   LISTA de incidentes recientes (tipo, viaje, quién atiende…)    ❌ NO — sólo el CONTADOR
 * ```
 *
 * ⇒ **`incidentesAbiertos` es un NÚMERO, no una lista.** La tabla "Incidentes recientes" de la
 * pantalla **no tiene fuente en este endpoint**. `Panel.tsx` la trae de `GET /consola/incidentes`
 * (real, ya cableado en `incidentesApi.ts` y usado por `Incidentes.tsx`) en una segunda llamada,
 * y lo dice en un comentario ahí mismo — no se dejó mock en silencio ni se inventó el dato.
 *
 * ⚠️ **NO hay ninguna cifra de DINERO, y es deliberado del backend, no un olvido.** `docs/01`
 * R-05: la plataforma no sabe cuánto se cobró un viaje (la tarifa es referencial y nunca se
 * cobra), así que no existe un ingreso que este resumen pueda mostrar sin inventarlo.
 *
 * ⚠️ **NO hay campos `Decimal` en esta respuesta.** Verificado contra el schema y el service
 * antes de tipar — la trampa `Prisma.Decimal → string` sobre HTTP ya mordió dos veces en este
 * repo (`reputacion`, `lat`/`lon`). Acá los ocho contadores son `Int` de Postgres (→ `number`) y
 * las tasas (`tasaVictoria`, `medianaTasa`, `p10Tasa`) las calcula el servicio en JS: son
 * `number` de verdad, no texto. Lo único que cambia de tipo al serializar es `desde`/`hasta` del
 * resumen (`Date` → ISO string), y por eso se reusa el tipo de `dispatchApi.ts`, que ya los tiene
 * tipados como `string` y ya fue verificado por efecto contra un backend real.
 *
 * ⚠️ **Grado de verificación, declarado:** el contrato está leído del `service` y del `DTO`, y
 * ejercitado por el test de integración HTTP del backend (`panel.integration.spec.ts`, controller
 * real + guards reales, con los ocho contadores y el reparto comprobados uno por uno). **NO**
 * hubo un `fetch` desde el bundle de la consola contra un `apps/api` vivo — mismo límite que ya
 * declaran `clienteApi.ts` y `posicionesApi.ts`.
 */
import { obtener } from './clienteApi';
import type { ResumenTasaVictoria } from './dispatchApi';

/**
 * Una fila del reparto. 🔑 **A diferencia de `TasaVictoriaConductor` de `dispatchApi.ts`, ESTA
 * SÍ trae `conductorNombre`**: el `PanelService` resuelve el nombre por join sobre `User` encima
 * del informe que ya calculó `TasaVictoriaService` (no lo recalcula). Cuando no hay `User`
 * —o su `nombre` es `null`— **cae al id, nunca a vacío**: es `string`, no `string | null`, y esa
 * decisión es del backend (ejercitada por su test "dato huérfano cae al id en vez de quedar
 * mudo"). Por eso la pantalla no necesita un `?? id` propio.
 *
 * ⚠️ Los nombres de campo **no** son los de `TasaVictoriaConductor`: acá es `viajesGanados`,
 * allá `ganadas`. Y este resumen expone TRES contadores, no los siete del informe crudo
 * (`perdidas`/`expiradas`/`rechazadas`/`competidas` se quedan en `/dispatch/tasa-victoria`).
 */
export interface FilaRepartoPanel {
  conductorId: string;
  /** Nunca vacío: si no hay `User` con nombre, el backend pone el propio id. */
  conductorNombre: string;
  ofertasRecibidas: number;
  viajesGanados: number;
  /** `null` cuando el conductor no COMPITIÓ ninguna vez: distingue "nunca compitió" de "perdió todas". */
  tasaVictoria: number | null;
}

/**
 * El resumen completo. Los ocho contadores son instantáneos ("ahora mismo"), **salvo dos**:
 * `viajesSinConductorHoy` mira el día calendario en curso, y el bloque `tasaVictoria` mira la
 * ventana `[desde, hasta)` (últimos 7 días por defecto). Mezclarlos al leer la pantalla es el
 * error fácil, y por eso las cifras del panel lo dicen en su nota.
 */
export interface PanelResumen {
  /** `estadoDisponibilidad === DISPONIBLE`. */
  conductoresDisponibles: number;
  /** `estadoDisponibilidad != DESCONECTADO` — o sea DISPONIBLE **+** OCUPADO. */
  conductoresActivos: number;
  /** Suscripciones en estado `ACTIVA`. NO es "conductores que pagaron": es el estado de la fila. */
  conductoresSuscritosActivos: number;
  /** Conductores en `EN_REVISION` — la misma cola que muestra `Verificacion.tsx`. */
  conductoresEnColaVerificacion: number;
  viajesBuscandoConductor: number;
  /** `ASIGNADO` + `EN_ORIGEN` + `EN_CURSO`. */
  viajesEnCurso: number;
  /**
   * `SIN_CONDUCTOR` solicitados desde el inicio del día de HOY.
   * ⚠️ El corte lo hace el reloj del PROCESO del backend en **UTC** (`setUTCHours(0,0,0,0)`), no
   * en hora de Lima: entre las 19:00 y la medianoche de Perú, "hoy" ya es el día siguiente para
   * este contador. Está documentado en el service; la pantalla no lo puede corregir sin
   * contradecir al backend, así que lo dice en la nota de la cifra en vez de disimularlo.
   */
  viajesSinConductorHoy: number;
  incidentesAbiertos: number;
  tasaVictoria: {
    /**
     * Reusa `ResumenTasaVictoria` de `dispatchApi.ts` a propósito: es **la misma interfaz del
     * backend** (`PanelService` inyecta y reexpone `TasaVictoriaService.informe()`, no lo
     * recalcula), y ese tipo ya está declarado con `desde`/`hasta` como ISO string y verificado
     * por efecto. Declararlo de nuevo acá crearía dos formas para un único contrato, que es
     * exactamente cómo se desincronizan.
     */
    resumen: ResumenTasaVictoria;
    reparto: FilaRepartoPanel[];
  };
}

/** Ventana de la tasa de victoria. Sin ella, el backend usa los últimos 7 días. */
export interface VentanaPanel {
  /** ISO 8601. Inclusivo. */
  desde?: string;
  /** ISO 8601. **Exclusivo**, igual que en auditoría. */
  hasta?: string;
}

/**
 * Sin `desde`/`hasta` el backend aplica su propio default (últimos 7 días, el mismo de
 * `/dispatch/tasa-victoria`). Se deja así: no hay todavía un selector de rango en la pantalla, y
 * adivinar acá un rango "equivalente" sería una segunda fuente de verdad para la misma pregunta
 * — mismo criterio que ya tomó `dispatchApi.ts`.
 */
export const obtenerPanel = (ventana: VentanaPanel = {}): Promise<PanelResumen> => {
  const parametros = new URLSearchParams();
  if (ventana.desde) parametros.set('desde', ventana.desde);
  if (ventana.hasta) parametros.set('hasta', ventana.hasta);
  const cadena = parametros.toString();
  return obtener<PanelResumen>(`/consola/panel${cadena === '' ? '' : `?${cadena}`}`);
};
